import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { WARootState } from '@/store/store';
import { AppMenuModel } from '@/models/AppViewModel';
import {
  translateDefaultGroup,
  translateDefaultView,
  getPageContextFromPath,
  PageContext,
} from '@/utils/pageDefaultsTranslation';

export interface PageDefaultsResult {
  defaultGroup: string;
  defaultView: string | undefined;
  rawDefaultGroup: string | undefined;
  rawDefaultView: string | undefined;
  menuItem: AppMenuModel | undefined;
  isMenuLoaded: boolean;
}

/**
 * Recursively searches the menu tree to find a menu item matching the given path.
 *
 * @param menu - The menu array to search
 * @param targetPath - The path to find (e.g., "/Portfolio/Accounts")
 * @returns The matching menu item, or undefined if not found
 */
function findMenuItemByPath(
  menu: AppMenuModel[] | undefined,
  targetPath: string,
): AppMenuModel | undefined {
  if (!menu || menu.length === 0) {
    return undefined;
  }

  const normalizedTarget = targetPath.toLowerCase().replace(/^\/+|\/+$/g, '');

  for (const item of menu) {
    // Normalize the menu item path
    const itemPath = (item.Path ?? '').toLowerCase().replace(/^\/+|\/+$/g, '');

    // Check for exact match or suffix match (menu paths often don't include role prefix)
    // Note: We require itemPath to be non-empty for suffix matches to avoid
    // the dashboard ("/") matching all paths since every string ends with ""
    const isMatch =
      itemPath === normalizedTarget ||
      (itemPath !== '' && normalizedTarget.endsWith(itemPath)) ||
      (normalizedTarget !== '' && itemPath.endsWith(normalizedTarget));

    if (item.Items && item.Items.length > 0) {
      const found = findMenuItemByPath(item.Items, targetPath);
      if (found) {
        return found;
      }
    }

    if (isMatch) {
      return item;
    }
  }

  return undefined;
}

/**
 * Extracts the page-specific path from a full route path.
 *
 * Routes in the new UI include role prefixes (e.g., "/investor/portfolio/accounts"),
 * while menu paths typically don't (e.g., "/Portfolio/Accounts").
 *
 * @param fullPath - The full route path from useLocation
 * @returns The page-specific path without role prefix
 */
function extractPagePath(fullPath: string): string {
  // Remove leading slash and split into segments
  const segments = fullPath.replace(/^\/+/, '').split('/');

  // Known role prefixes that should be stripped
  const rolePrefix = ['investor', 'advisor', 'firm', 'administrator', 'estate'];

  // If the first segment is a role prefix, remove it
  if (segments.length > 0 && rolePrefix.includes(segments[0].toLowerCase())) {
    segments.shift();
  }

  // Reconstruct the path
  return '/' + segments.join('/');
}

/**
 * Hook to get the firm-configured default grouping and view settings for the current page.
 *
 * This hook:
 * 1. Gets the current route path
 * 2. Finds the matching menu item from userInfo.Menu
 * 3. Extracts and translates the DefaultGroup/DefaultView values
 *
 * @param overridePath - Optional path override (useful for widgets that know their page context)
 * @param pageContext - Optional page context override for translation
 * @returns The page defaults including translated grouping and view values
 *
 * @example
 * // In a page component
 * const { defaultGroup, isMenuLoaded } = usePageDefaults();
 *
 * // With explicit path for widgets
 * const { defaultGroup } = usePageDefaults("/Portfolio/Accounts", "accounts");
 *
 * // Initialize state with default
 * const [activeGroup, setActiveGroup] = useState<GroupOption>(() => {
 *   const match = GROUP_OPTIONS.find(opt => opt.value === defaultGroup);
 *   return match ?? GROUP_OPTIONS[0];
 * });
 */
export function usePageDefaults(
  overridePath?: string,
  pageContext?: PageContext,
): PageDefaultsResult {
  const location = useLocation();
  const menu = useSelector(
    (state: WARootState) => state.appView.UserInfo?.Menu,
  );

  const result = useMemo(() => {
    const currentPath = overridePath ?? extractPagePath(location.pathname);

    const menuItem = findMenuItemByPath(menu, currentPath);

    // Determine page context for translation
    const context = pageContext ?? getPageContextFromPath(currentPath);

    const rawDefaultGroup = menuItem?.DefaultGroup;
    const rawDefaultView = menuItem?.DefaultView;

    const translatedGroup = context
      ? translateDefaultGroup(rawDefaultGroup, context)
      : 'none';
    const translatedView = translateDefaultView(rawDefaultView, context);

    return {
      defaultGroup: translatedGroup,
      defaultView: translatedView,
      rawDefaultGroup,
      rawDefaultView,
      menuItem,
      isMenuLoaded: menu !== undefined && menu.length > 0,
    };
  }, [location.pathname, menu, overridePath, pageContext]);

  return result;
}

export default usePageDefaults;
